import React, {
  useEffect,
  useState,
  useRef,
  useCallback,
  useActionState,
} from "react";
import { IProductSearchParams, productApi } from "../services/api/productApi";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { cartApi } from "../services/api/cartApi";
import { ordersApi } from "../services/api/orderApi";
import { useAuth } from "../contexts/AuthContext";
import Carousel from "react-multi-carousel";
import "react-multi-carousel/lib/styles.css";
import { toast } from "react-toastify";
import { wishlistApi } from "../services/api/wishlistApi";
import { showError, showSuccess } from "../lib/toast";
import { notifyWishlistUpdate } from "../utils/wishlistUtils";
import { notifyCartUpdate } from "../utils/cartUpdateUtils";
import ImageWithLoader from "../components/ImageWithLoader";
import ScrollToTop from "../components/ScrollToTop";
import ProductDetailsWithTabs from "./Productdetailswithtabs ";

type materialObj = {
  name: String;
  _id: String;
};
const ProductDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [searchParams] = useSearchParams();

  const activeCollection = searchParams.get("collection");
  const { isLoggedIn } = useAuth();
  const [product, setProduct] = useState<any>(null);
  const [relatedProducts, setRelatedProducts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [relatedLoading, setRelatedLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [products, setProducts] = useState<any[]>([]);
  const [wishlist, setWishlist] = useState<string[]>([]);
  const [wishlistLoading, setWishlistLoading] = useState<{
    [key: string]: boolean;
  }>({});
  const [productsLoading, setProductsLoading] = useState(false);
  const [addingToCart, setAddingToCart] = useState(false);
  const [isOwned, setIsOwned] = useState(false);
  const [checkingOwnership, setCheckingOwnership] = useState(false);
  const exploreCollectionCarouselRef = useRef<any>(null);
  const sameArtistCarouselRef = useRef<any>(null);
  const mainCarouselRef = useRef<any>(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [showFullImage, setShowFullImage] = useState(false);
  const [sizes, setSizes] = useState([]);
  const [canvasWrap, setCanvasWrap] = useState<String[]>([]);
  const [frame, setFrame] = useState<Array<string>>([]);
  const [selectedFrame, setSelectedFrame] = useState<any>(null);
  const [selectedSize, setSelectedSize] = useState<any>(null);
  const [selectedCanvas, setSelectedCanvas] = useState<any>(null);
  const [printMaterial, setPrintMaterial] = useState<materialObj[]>([]);
  const [speakerFlag, setSpeakerFlag] = useState(false)
  const [selectedPrintMaterial, setSelectedPrintMaterial] =
    useState<materialObj | null>(null);

  // Accordion state
  const [accordionState, setAccordionState] = useState({
    artistDetails: true,
    description: true,
    digitalFileDetails: true,
    downloadAndUse: true,
  });

  const toggleAccordion = (section: keyof typeof accordionState) => {
    setAccordionState((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const fetchWishlist = useCallback(async () => {
    // Only fetch wishlist if user is logged in
    if (!isLoggedIn) {
      setWishlist([]);
      return;
    }

    try {
      const wishlistResponse: any = await wishlistApi.getWishlist();
      setWishlist(
        wishlistResponse?.items?.map((item: any) => item?.product?._id) || [],
      );
    } catch (error) {
      console.error("Error fetching wishlist:", error);
      setWishlist([]);
    }
  }, [isLoggedIn]);
  const checkProductOwnership = useCallback(
    async (productId: string) => {
      if (!isLoggedIn) {
        setIsOwned(false);
        return;
      }

      try {
        setCheckingOwnership(true);
        const response: any = await ordersApi.checkProductOwnership(productId);
        if (response.success && response.data) {
          setIsOwned(response.data.owned);
        }
      } catch (error) {
        console.error("Error checking product ownership:", error);
        setIsOwned(false);
      } finally {
        setCheckingOwnership(false);
      }
    },
    [isLoggedIn],
  );

  useEffect(() => {
    const fetchProductAndRelated = async () => {
      if (!id) return;

      try {
        setLoading(true);
        // Fetch product details
        await fetchWishlist();
        const productResponse = await productApi.getProductById(id);
        console.log("product response", productResponse);
        setProduct(productResponse.data);
        setFrame(productResponse.data.frame);
        setSizes(productResponse.data.size);
        setCanvasWrap(productResponse.data.canvasWrap);
        setPrintMaterial(productResponse.data.printMaterial);
        setSelectedFrame(productResponse.data.frame[0]);
        setSelectedCanvas(productResponse.data.canvasWrap[0]);
        setSelectedPrintMaterial(productResponse.data.printMaterial[0]);
        setSelectedSize(productResponse.data.size[0]);

        // Check if user owns this product
        if (isLoggedIn) {
          await checkProductOwnership(id);
        }

        // Fetch related products
        setRelatedLoading(true);
        const relatedResponse = await productApi.getProductsByArtistId(
          productResponse?.data?.artistId?._id,
        );
        setRelatedProducts(relatedResponse.data.products || []);
      } catch (err) {
        console.error("Error fetching product data:", err);
        setError("Failed to load product details");
      } finally {
        setLoading(false);
        setRelatedLoading(false);
      }
    };

    fetchProductAndRelated();
  }, [id, isLoggedIn, fetchWishlist, checkProductOwnership]);

  // Scroll to top when product ID changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [id]);

  const fetchProducts = useCallback(
    async (params: IProductSearchParams = {}) => {
      try {
        setProductsLoading(true);
        const response = await productApi.getProducts(params);
        setProducts(response.data.products || []);
      } catch (err) {
        console.error("Error fetching products:", err);
        setProducts([]);
      } finally {
        setProductsLoading(false);
      }
    },
    [],
  );

  // Initial fetch for products
  useEffect(() => {
    fetchProducts({
      limit: 12, // Default limit without pagination
      // Add any default filters here
    });
  }, [fetchProducts]);

  const speak = () => {
    const voices = window.speechSynthesis.getVoices();

    if (!voices.length) {
      console.log("Voices not loaded yet");
      return;
    }
    const femaleVoice = voices.find(
      (v) =>
        v.lang.toLowerCase().includes("en") &&
        v.name.toLowerCase().includes("google uk english female"),
    );

    const voice =
      femaleVoice ||
      voices.find((v) => v.lang.toLowerCase().includes("en")) ||
      voices[0];

    // Combine the text from your accordion
    const artistText = `Artist Details. ${product?.artistId?.name} – ${product?.artistId?.subtitle}. ${product?.artistId?.description}`;

    const descriptionText = `Description. ${product?.name} is a modern digital artwork that captures the calmness of twilight blending into the mysteries of night. With deep blues and soft amber tones, it evokes both serenity and curiosity — making it a perfect piece for contemporary spaces. This painting symbolizes the balance between endings and new beginnings, created with digital brushwork that blends fine detail with abstract freedom.`;

    const fullText = `${artistText}. ${descriptionText}`;

    const utterance = new SpeechSynthesisUtterance(fullText);
    utterance.voice = voice;
    utterance.lang = voice.lang;
    utterance.rate = 1;
    utterance.pitch = 1;
    utterance.onend = () => {
      setSpeakerFlag(false);
    };

    utterance.onerror = () => {
      setSpeakerFlag(false);
    };

    console.log("Using voice:", voice.name);

    window.speechSynthesis.cancel();
    window.speechSynthesis.speak(utterance);
  };
  const handleFirstClick = () => {
    if (window.speechSynthesis.speaking) {
      setSpeakerFlag(false);
      window.speechSynthesis.cancel();
      return;
    }

    const voices = window.speechSynthesis.getVoices();

    if (voices.length) {
      setSpeakerFlag(true);
      speak();
    } else {

      window.speechSynthesis.addEventListener(
        "voiceschanged",
        () => {
          setSpeakerFlag(true);
          speak();
        },
        { once: true }
      );
    }
  };
  useEffect(() => {
    const handleBeforeUnload = () => {
      window.speechSynthesis.cancel();
      setSpeakerFlag(false);
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, []);

  useEffect(() => {
    return () => {
      window.speechSynthesis.cancel();
    };
  }, []);

  const goToPreviousExplore = () => {
    if (exploreCollectionCarouselRef.current) {
      exploreCollectionCarouselRef.current.previous();
    }
  };

  const goToNextExplore = () => {
    if (exploreCollectionCarouselRef.current) {
      exploreCollectionCarouselRef.current.next();
    }
  };

  const goToPreviousSameArtist = () => {
    if (sameArtistCarouselRef.current) {
      sameArtistCarouselRef.current.previous();
    }
  };

  const goToNextSameArtist = () => {
    if (sameArtistCarouselRef.current) {
      sameArtistCarouselRef.current.next();
    }
  };
  const isProductInWishlist = (productId: string) => {
    return wishlist.some((item) => item === productId);
  };
  const convertInchesToCm = (size: string): string => {
    // Example: "7.9 x 5.3 inches"
    const numbers = size.replace("inches", "").trim().split("x");
    if (numbers.length !== 2) return size; // fallback

    const widthInCm = (parseFloat(numbers[0].trim()) * 2.54).toFixed(1);
    const heightInCm = (parseFloat(numbers[1].trim()) * 2.54).toFixed(1);

    return `${widthInCm} x ${heightInCm} cm`;
  };
  const handleWishlistClick = async (productId: string) => {
    if (!isLoggedIn) {
      showError("Please login to add items to wishlist");
      navigate("/login");
      return;
    }

    if (wishlistLoading[productId]) return; // Prevent multiple clicks

    setWishlistLoading((prev) => ({ ...prev, [productId]: true }));

    try {
      const isInWishlist = isProductInWishlist(productId);
      if (isInWishlist) {
        await wishlistApi.removeFromWishlist(productId);
        setWishlist((prev) => prev.filter((item) => item !== productId));
        showSuccess("Removed from wishlist");
        notifyWishlistUpdate(); // Notify wishlist drawer to refresh
      } else {
        await wishlistApi.addToWishlist(productId);
        setWishlist((prev) => [...prev, productId]);
        showSuccess("Added to wishlist");
        notifyWishlistUpdate(); // Notify wishlist drawer to refresh
      }
    } catch (error: any) {
      console.error("Wishlist update error:", error);
      showError(error.message || "Failed to update wishlist");
    } finally {
      setWishlistLoading((prev) => ({ ...prev, [productId]: false }));
    }
  };
  if (loading) {
    return (
      <>
        <ScrollToTop />
        <div className="container py-5 text-center">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-3">Loading product details...</p>
        </div>
      </>
    );
  }

  const handleAddToCart = async (productId?: string, isPrint?: boolean) => {
    const targetProductId = product?._id;

    if (!targetProductId) {
      return;
    }

    // Check if user already owns the main product
    if (targetProductId === product?._id && isOwned) {
      toast.info(
        "You already own this product! Check your previous purchases.",
      );
      navigate("/previous-purchase");
      return;
    }

    // Check if user is logged in
    if (!isLoggedIn) {
      toast.error("Please login to add items to cart");
      navigate("/login");
      return;
    }

    try {
      // setAddingToCart(true);
      // const cartData = {
      //   productId: targetProductId,
      //   quantity: 1,
      // };
      let cartData;
      if (isPrint) {
        cartData = {
          productId: targetProductId,
          isPrint: true,
          selectedSize: {
            name: `${selectedSize?.name} Inch`,
            price: selectedSize?.price || "",
          },

          selectedFrame: {
            name: selectedFrame.name,
            price: selectedFrame.price,
          },

          selectedCanvasWrap: {
            name: selectedCanvas.name,
          },

          selectedPrintMaterial: {
            name: selectedPrintMaterial?.name,
          },
        };
      } else {
        cartData = {
          productId: targetProductId,
          quantity: 1,
          isPrint: false,
        };
      }

      await cartApi.addToCart(cartData);
      notifyCartUpdate();

      toast.success("Product added to cart successfully!");
    } catch (error: any) {
      console.error("Error adding to cart:", error);
      console.error("Error response:", error?.response);
      console.error("Error status:", error?.response?.status);
      console.error("Error data:", error?.response?.data);

      // Handle specific error cases
      if (error?.response?.status === 401) {
        toast.error("Please login to add items to cart");
        navigate("/login");
      } else if (error?.response?.status === 400) {
        const errorMsg = error?.response?.data?.message || "Invalid request";
        toast.error(errorMsg);
        // Check if it's an ownership error and redirect
        if (errorMsg.includes("already own")) {
          setTimeout(() => navigate("/previous-purchase"), 1500);
        }
      } else if (error?.response?.status === 404) {
        toast.error("Product not found");
      } else {
        toast.error("Failed to add product to cart. Please try again.");
      }
    } finally {
      setAddingToCart(false);
    }
  };

  const handleBuyNow = async (isPrint: boolean = false) => {
    const targetProductId = product?._id;

    if (!targetProductId) {
      return;
    }

    // Check if user already owns the main product
    if (targetProductId === product?._id && isOwned) {
      toast.info(
        "You already own this product! Check your previous purchases.",
      );
      navigate("/previous-purchase");
      return;
    }

    if (!isLoggedIn) {
      toast.error("Please login to purchase");
      navigate("/login");
      return;
    }

    try {
      setAddingToCart(true);
      let cartData: any;

      if (isPrint) {
        cartData = {
          productId: targetProductId,
          isPrint: true,
          selectedSize: {
            name: `${selectedSize?.name} Inch`,
            price: selectedSize?.price || "",
          },

          selectedFrame: {
            name: selectedFrame.name,
            price: selectedFrame.price,
          },

          selectedCanvasWrap: {
            name: selectedCanvas.name,
          },

          selectedPrintMaterial: {
            name: selectedPrintMaterial?.name,
          },
        };
      } else {
        cartData = {
          productId: targetProductId,
          quantity: 1,
          isPrint: false,
        };
      }

      await cartApi.addToCart(cartData);
      notifyCartUpdate();
      toast.success("Product added to cart!");

      // Navigate to checkout for immediate purchase
      navigate("/cart");
    } catch (error: any) {
      console.error("Error during buy now:", error);

      if (error?.response?.status === 401) {
        toast.error("Please login to purchase");
        navigate("/login");
      } else if (error?.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error("Failed to process purchase. Please try again.");
      }
    } finally {
      setAddingToCart(false);
    }
  };

  if (error) {
    return (
      <div className="container py-5 text-center">
        <div className="alert alert-danger" role="alert">
          <h4 className="alert-heading">Error!</h4>
          <p>{error}</p>
          <hr />
          <p className="mb-0">
            <a href="/" className="btn btn-primary">
              Return to Home
            </a>
          </p>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="container py-5 text-center">
        <div className="alert alert-warning" role="alert">
          <h4 className="alert-heading">Product Not Found</h4>
          <p>The requested product could not be found.</p>
          <hr />
          <p className="mb-0">
            <a href="/" className="btn btn-primary">
              Return to Home
            </a>
          </p>
        </div>
      </div>
    );
  }

  const sliderImages = Array.from(
    new Set([product?.image, ...(product?.subImages || [])]),
  ).filter(
    (img): img is string => typeof img === "string" && img.trim() !== "",
  );

  return (
    <>
      <ScrollToTop />
      {/* Page Navigation */}

      <div className="collection_details_templet_banner d-sm-block d-none">
        <div className="page_navegation_area">
          <div className="container">
            <div className="page_navegation font_two">
              <p>
                <a href="/">Home</a> /{" "}
                <span className="active fw_700">Canvas Collection</span>
              </p>
            </div>
          </div>
        </div>
      </div>
      {activeCollection == "Canvas" && (
        <ProductDetailsWithTabs
          product={product}
          isOwned={isOwned}
          isLoggedIn={isLoggedIn}
          addingToCart={addingToCart}
          checkingOwnership={checkingOwnership}
          handleBuyNow={handleBuyNow}
          handleAddToCart={handleAddToCart}
          handleWishlistClick={handleWishlistClick}
          isProductInWishlist={isProductInWishlist}
          navigate={navigate}
          convertInchesToCm={convertInchesToCm}
          ImageWithLoader={ImageWithLoader}
          sizes={sizes}
          canvasWrap={canvasWrap}
          frame={frame}
          selectedFrame={selectedFrame}
          selectedSize={selectedSize}
          selectedCanvas={selectedCanvas}
          setSelectedFrame={setSelectedFrame}
          setSelectedSize={setSelectedSize}
          setSelectedCanvas={setSelectedCanvas}
          printMaterial={printMaterial}
          selectedPrintMaterial={selectedPrintMaterial}
          setSelectedPrintMaterial={setSelectedPrintMaterial}
          handleSoundClick={handleFirstClick}
          speakerFlag={speakerFlag}
        />
      )}
      {!activeCollection && (
        <section id="product_collection_details_1">
          <div className="container">
            <div className="row">
              {/* Left Column - Product Images */}
              <div className="col-xl-6 mb-4 mb-xl-0">
                <div className="d-flex gap-1 flex-column flex-md-row">
                  {/* Main Image - First in DOM order */}
                  <div
                    className="position-relative overflow-hidden order-1 order-md-2"
                    style={{ flex: 1 }}
                  >
                    <Carousel
                      ref={mainCarouselRef}
                      arrows={sliderImages.length > 1}
                      swipeable={true}
                      draggable={true}
                      showDots={false}
                      infinite={false}
                      responsive={{
                        desktop: {
                          breakpoint: { max: 3000, min: 1024 },
                          items: 1,
                        },
                        tablet: {
                          breakpoint: { max: 1024, min: 464 },
                          items: 1,
                        },
                        mobile: {
                          breakpoint: { max: 464, min: 0 },
                          items: 1,
                        },
                      }}
                      beforeChange={(nextSlide) => {
                        setCurrentImageIndex(nextSlide);
                      }}
                    >
                      {sliderImages.map((img, idx) => (
                        <div key={idx} className="position-relative">
                          <ImageWithLoader
                            src={img}
                            alt={`product_detail_img_${idx}`}
                            className="w-100"
                            style={{
                              width: "100%",
                              height: "auto",
                              maxHeight: "802px",
                              objectFit: "contain",
                              backgroundColor: "#f8f9fa",
                            }}
                          />
                          <button
                            onClick={() => setShowFullImage(true)}
                            style={{
                              position: "absolute",
                              bottom: "20px",
                              right: "20px",
                              width: "46px",
                              height: "46px",
                              background: "#FFFFFF",
                              border: "none",
                              borderRadius: "8px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.1)",
                              zIndex: 10,
                              cursor: "pointer",
                            }}
                          >
                            <svg
                              width="24"
                              height="24"
                              viewBox="0 0 24 24"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M15 3H21V9"
                                stroke="#112C3B"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                              <path
                                d="M9 21H3V15"
                                stroke="#112C3B"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                              <path
                                d="M21 3L14 10"
                                stroke="#112C3B"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                              <path
                                d="M3 21L10 14"
                                stroke="#112C3B"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          </button>
                        </div>
                      ))}
                    </Carousel>
                  </div>

                  {/* Thumbnails - Second in DOM but using order classes to control position */}
                  {sliderImages.length > 1 && (
                    <div
                      className="d-flex flex-row flex-md-column gap-2 overflow-auto mt-3 mt-md-0 order-2 order-md-1"
                      style={{
                        maxHeight: "802px",
                        width: "auto",
                        maxWidth: "100%",
                        flexShrink: 0,
                      }}
                    >
                      {sliderImages.map((img, idx) => (
                        <div
                          key={idx}
                          className={`thumbnail_item ${idx === currentImageIndex ? "active" : ""}`}
                          onClick={() => {
                            setCurrentImageIndex(idx);
                            mainCarouselRef.current.goToSlide(idx);
                          }}
                          style={{
                            width: "80px",
                            height: "80px",
                            cursor: "pointer",
                            border:
                              idx === currentImageIndex
                                ? "2px solid #112C3B"
                                : "1px solid #E5E7EB",
                            borderRadius: "8px",
                            overflow: "hidden",
                            transition: "all 0.2s ease",
                            flexShrink: 0,
                          }}
                        >
                          <img
                            src={img}
                            alt={`thumbnail_${idx}`}
                            style={{
                              width: "100%",
                              height: "100%",
                              objectFit: "cover",
                              opacity: idx === currentImageIndex ? 1 : 0.6,
                            }}
                          />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Right Side: Product Info */}
              <div className="col-xl-6">
                <div className=" font_two">
                  <div className="product_collection_details_title_area d-sm-flex justify-content-sm-between d-grid">
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "16px",
                        flexWrap: "wrap",
                        cursor: "pointer",
                      }}
                    >
                      <h2 className="collection_details_heading font_one">
                        {product?.name}
                      </h2>
                      <img
                        src={speakerFlag ? "/assets/images/volume-on.svg" : '/assets/images/volume-off.svg'}
                        width={30}
                        height={30}
                        onClick={handleFirstClick}
                        style={{ cursor: "pointer" }}
                      />
                    </div>

                    <div
                      className="payment_details_btn_area"
                      style={{ display: "flex", alignItems: "center" }}
                    >
                      {isOwned ? (
                        <button
                          type="button"
                          className="blue_btn"
                          onClick={(e) => {
                            e.preventDefault();

                            e.stopPropagation();

                            navigate("/previous-purchase");
                          }}
                          style={{
                            background: "#10B981",

                            borderColor: "#10B981",
                          }}
                        >
                          ✓ Owned - View Purchase
                        </button>
                      ) : (
                        <>
                          <button
                            type="button"
                            className="blue_btn"
                            onClick={(e) => {
                              e.preventDefault();

                              e.stopPropagation();

                              handleBuyNow();
                            }}
                            disabled={addingToCart || checkingOwnership}
                          >
                            {addingToCart ? "Processing..." : "Buy Now"}
                          </button>

                          <button
                            type="button"
                            className="border_btn"
                            style={{ minWidth: "131px" }}
                            onClick={(e) => {
                              e.preventDefault();

                              e.stopPropagation();

                              handleAddToCart();
                            }}
                            disabled={addingToCart || checkingOwnership}
                          >
                            {addingToCart ? "Adding..." : "Add to Cart"}
                          </button>
                        </>
                      )}
                      {isLoggedIn && (
                        <a
                          href="#"
                          onClick={(e) => {
                            handleWishlistClick(product?._id);
                          }}
                          className="product_like_btn "
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="24"
                            height="24"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <path
                              d="M18.8569 3.74471C14.8652 1.60384 11.9991 4.75972 11.9991 4.75972C11.9991 4.75972 9.13283 1.60383 5.14113 3.7447C0.306417 6.3377 -0.0407534 16.492 11.9991 21C24.0389 16.492 23.6916 6.33771 18.8569 3.74471Z"
                              stroke="#112C3B"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                              fill={
                                isProductInWishlist(product._id)
                                  ? "#112C3B"
                                  : "none"
                              }
                            />
                          </svg>
                        </a>
                      )}
                    </div>
                  </div>

                  {/*<p className="text_16 fw_500">By Digital Artist</p>*/}

                  <p className="collection_details_price fw_700 d-sm-inline-block d-none">
                    ${product?.price?.toFixed(2) || "0.00"}
                  </p>

                  <p className="text_20 text-muted">
                    <span className="fw_700">Dimensions: </span>
                    {convertInchesToCm(
                      product?.fileDetails.recommendedPrintSize,
                    )}{" "}
                  </p>

                  {/* Accordion / Info */}

                  <div className="product_details_accordion_area">
                    <div className="product_accordion accordion">
                      <div className="accordion-item">
                        <div className="accordion-header font_one heading_text_6 text-uppercase active">
                          ARTIST DETAILS
                        </div>

                        <div className="accordion-content open">
                          <span className="fw_700 text_16">
                            {product?.artistId?.name} –{" "}
                            {product?.artistId?.subtitle}
                          </span>

                          <p className="text_16 m-0">
                            {product?.artistId?.description}
                          </p>
                        </div>
                      </div>

                      <div className="accordion-item">
                        <div className="accordion-header font_one heading_text_6 text-uppercase active">
                          description
                        </div>

                        <div className="accordion-content open">
                          <p className="text_16 m-0">
                            <span className="fw_700">{product?.name}</span> is a
                            modern digital artwork that captures the{" "}
                            <span className="fw_700">calmness</span> of twilight
                            blending into the mysteries of night. With deep
                            blues and soft amber tones, it evokes both serenity
                            and curiosity — making it a perfect piece for
                            contemporary spaces.
                          </p>

                          <p className="text_16 m-0">
                            This painting{" "}
                            <span className="fw_700">
                              symbolizes the balance between endings and new
                              beginnings
                            </span>
                            , created with digital brushwork that blends fine
                            detail with abstract freedom.
                          </p>
                        </div>
                      </div>

                      {product?.fileDetails && (
                        <div className="accordion-item">
                          <div className="accordion-header font_one heading_text_6 text-uppercase active">
                            Digital File Details
                          </div>

                          <div className="accordion-content open">
                            <p className="text_16">
                              <span className="fw_700">Format:</span>{" "}
                              High-resolution JPG & PNG
                            </p>

                            <p className="text_16">
                              <span className="fw_700">Resolution:</span>{" "}
                              {product?.fileDetails?.resolution || "N/A"}
                            </p>

                            <p className="text_16">
                              <span className="fw_700">
                                Recommended Print Size:
                              </span>{" "}
                              Up to{" "}
                              {product?.fileDetails?.recommendedPrintSize ||
                                "N/A"}
                            </p>

                            <p className="text_16">
                              <span className="fw_700">File Size:</span>{" "}
                              {product?.fileDetails?.fileSize || "N/A"}
                            </p>
                          </div>
                        </div>
                      )}

                      <div className="accordion-item">
                        <div className="accordion-header font_one heading_text_6 text-uppercase active">
                          Download & Use
                        </div>

                        <div className="accordion-content open">
                          <p className="text_16">
                            Instant download after checkout
                          </p>

                          <p className="text_16">
                            Can be printed at home or through any professional
                            printing service
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="payment_details_box d-sm-flex d-none justify-content-between">
                    <div className="payment_details_btn_inner"></div>

                    {product?.digitalContent && (
                      <a
                        href={product.digitalContent}
                        className="product_download_btn"
                      >
                        <img
                          src="/assets/images/product_download_btn.svg"
                          alt="product_download_btn"
                        />
                      </a>
                    )}
                  </div>

                  <ul className="payment_point_list text_16">
                    <li className="mb-1">
                      <span style={{ marginRight: "10px" }}>
                        <img
                          src="/assets/images/payment_point_icon.svg"
                          alt="payment_point_icon"
                        />
                      </span>
                      Secure Payments |{" "}
                      <a href="#" className="fw_500">
                        Multiple Options Available
                      </a>
                    </li>

                    <li className="mb-1">
                      <span style={{ marginRight: "10px" }}>
                        <img
                          src="/assets/images/payment_point_icon.svg"
                          alt="payment_point_icon"
                        />
                      </span>
                      (Instant digital download after purchase)
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Product Collection Details Section */}

      {/* Mobile Payment Area */}
      <div className="mobile_payment_area d-sm-none d-grid">
        <button
          type="button"
          className="fev_btn_responsive border-0 bg-transparent p-0"
        >
          <img
            src="/assets/images/fev_btn_responsive.svg"
            alt="fev_btn_responsive"
          />
        </button>
        {isOwned ? (
          <button
            className="blue_btn"
            onClick={(e) => {
              e.preventDefault();
              navigate("/previous-purchase");
            }}
            style={{
              background: "#10B981",
              borderColor: "#10B981",
            }}
          >
            ✓ Owned - View Purchase
          </button>
        ) : (
          <button
            className="blue_btn"
            onClick={(e) => {
              e.preventDefault();
              handleBuyNow();
            }}
            disabled={addingToCart || checkingOwnership}
          >
            {addingToCart ? "Processing..." : "Buy Now"}
          </button>
        )}
      </div>

      {/* From the Same Artist Section */}
      <section className="same_artist_section">
        <div className="same_artist_sec">
          <div className="container">
            <div className="digi_packs_area same_artist_area">
              <div className="row">
                <div className="col-12">
                  <div className="same_artist_title_area common_title_area d-flex justify-content-between align-items-center">
                    <div className="sec_title_box">
                      <h2 className="heading_text_2 mb-1">
                        From the Same Artist
                      </h2>
                      <p className="font_two">
                        The journey doesn't end here. Discover other paintings
                        from the artist that reflect unique emotions, colors,
                        and visions.
                      </p>
                    </div>
                    <div className="title_arrow_box d-lg-flex d-none">
                      <button
                        onClick={goToPreviousSameArtist}
                        className="arrow-btn"
                        aria-label="Previous"
                      >
                        <img
                          src="/assets/images/nav_arrow_left.png"
                          alt="Previous"
                        />
                      </button>
                      <button
                        onClick={goToNextSameArtist}
                        className="arrow-btn"
                        aria-label="Next"
                      >
                        <img
                          src="/assets/images/nav_arrow_right.png"
                          alt="Next"
                        />
                      </button>
                    </div>
                  </div>
                </div>
                <div className="col-12">
                  {relatedLoading ? (
                    <div className="text-center py-4 h-50">
                      <div className="spinner-border text-dark" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                      <p className="mt-2 font_two">Loading...</p>
                    </div>
                  ) : (
                    <Carousel
                      ref={sameArtistCarouselRef}
                      additionalTransfrom={0}
                      arrows={false}
                      autoPlay={true}
                      autoPlaySpeed={4000}
                      infinite
                      responsive={{
                        desktop: {
                          breakpoint: { max: 3000, min: 1024 },
                          items: 3,
                          slidesToSlide: 1,
                        },
                        tablet: {
                          breakpoint: { max: 1024, min: 464 },
                          items: 2,
                          slidesToSlide: 1,
                        },
                        mobile: {
                          breakpoint: { max: 464, min: 0 },
                          items: 1,
                          slidesToSlide: 1,
                        },
                      }}
                      showDots={false}
                      keyBoardControl
                      customTransition="transform 500ms ease-in-out"
                      transitionDuration={500}
                      containerClass="digi_packs_content_list same_artist_slider_list"
                      itemClass="col-lg-4 col-sm-6 digi_packs_carousel_item"
                    >
                      {relatedProducts.map((product) => (
                        <div
                          key={product._id}
                          className="most_selling_art_box digi_packs_box d-flex flex-column"
                        >
                          <div className="most_selling_art_box_img position-relative">
                            {isLoggedIn && (
                              <button
                                type="button"
                                className="like_icon border-0 bg-transparent p-0"
                                onClick={(e) => {
                                  e.preventDefault(); // Prevent default anchor behavior
                                  e.stopPropagation(); // Prevent event bubbling
                                  handleWishlistClick(product?._id);
                                }}
                                aria-label="Add to wishlist"
                              >
                                <svg
                                  width="46"
                                  height="46"
                                  viewBox="0 0 46 46"
                                  fill="none"
                                  xmlns="http://www.w3.org/2000/svg"
                                >
                                  <rect
                                    width="46"
                                    height="46"
                                    rx="23"
                                    fill="#FAF6EE"
                                  />
                                  <path
                                    d="M29.8569 14.7447C25.8652 12.6038 22.9991 15.7597 22.9991 15.7597C22.9991 15.7597 20.1328 12.6038 16.1411 14.7447C11.3064 17.3377 10.9592 27.492 22.9991 32C35.0389 27.492 34.6916 17.3377 29.8569 14.7447Z"
                                    stroke="#112C3B"
                                    strokeWidth="1.5"
                                    strokeLinecap="round"
                                    fill={
                                      isProductInWishlist(product._id)
                                        ? "#112C3B"
                                        : "none"
                                    }
                                  />
                                </svg>
                              </button>
                            )}

                            <ImageWithLoader
                              src={
                                product.image || "/assets/images/nav_img.png"
                              }
                              alt={product.name}
                              className="w-100 object-cover"
                              style={{
                                width: "460px",
                                height: "250px",
                                cursor: "pointer",
                              }}
                              onClick={() =>
                                navigate(`/product/${product._id}`)
                              }
                            />

                            <div className="common_multi_btn_area">
                              <button
                                type="button"
                                className="blue_btn light_btn"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  handleAddToCart(product._id);
                                }}
                                disabled={addingToCart}
                              >
                                {addingToCart ? "Adding..." : "Add to Cart"}
                              </button>
                              <button
                                type="button"
                                className="blue_btn"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  handleBuyNow(product._id);
                                }}
                                disabled={addingToCart}
                              >
                                {addingToCart ? "Processing..." : "Buy Now"}
                              </button>
                            </div>
                          </div>

                          <div className="nav_inner_details_box_info font_two color_darkblue">
                            <p className="fw_500">{product.name}</p>
                            <p className="nav_inner_details_info fw_500">
                              {product.artistId.name},{" "}
                              {product?.artistId?.country}
                            </p>
                            <div className="nav_inner_details_price_box">
                              <span className="nav_box_price fw_700">
                                ${product.price}
                              </span>
                              <span className="nav_box_cm fw_500">
                                {convertInchesToCm(
                                  product?.fileDetails.recommendedPrintSize,
                                )}
                              </span>
                              {/*{product.fileDetails?.recommendedPrintSize && (*/}
                              {/*  <span className="nav_box_cm fw_500">*/}
                              {/*    {product.fileDetails.recommendedPrintSize}*/}
                              {/*  </span>*/}
                              {/*)}*/}
                            </div>
                          </div>
                        </div>
                      ))}
                    </Carousel>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Explore Collection Section */}
      <section className="digi_packs_section" id="explore_collextion_sec">
        <div className="digi_packs_sec">
          <div className="container">
            <div className="digi_packs_area">
              <div className="row" style={{ marginBottom: "50px" }}>
                <div className="col-12">
                  <div className="digi_packs_title_area common_title_area d-flex justify-content-between align-items-center">
                    <div className="sec_title_box">
                      <h2 className="heading_text_2 mb-1">
                        Explore Collection
                      </h2>
                      <p className="font_two">
                        Dive into the collection and experience art beyond a
                        single canvas.
                      </p>
                    </div>
                    <div className="title_arrow_box d-lg-flex d-none">
                      <button
                        onClick={goToPreviousExplore}
                        className="arrow-btn"
                        aria-label="Previous"
                      >
                        <img
                          src="/assets/images/nav_arrow_left.png"
                          alt="Previous"
                        />
                      </button>
                      <button
                        onClick={goToNextExplore}
                        className="arrow-btn"
                        aria-label="Next"
                      >
                        <img
                          src="/assets/images/nav_arrow_right.png"
                          alt="Next"
                        />
                      </button>
                    </div>
                  </div>
                </div>
                <div className="col-12">
                  {productsLoading ? (
                    <div className="text-center py-4 h-50">
                      <div className="spinner-border text-dark" role="status">
                        <span className="visually-hidden">
                          Loading products...
                        </span>
                      </div>
                      <p className="mt-2 font_two">Loading products...</p>
                    </div>
                  ) : (
                    <Carousel
                      ref={exploreCollectionCarouselRef}
                      additionalTransfrom={0}
                      arrows={false}
                      autoPlay={true}
                      autoPlaySpeed={4000}
                      infinite
                      responsive={{
                        desktop: {
                          breakpoint: { max: 3000, min: 1024 },
                          items: 3,
                          slidesToSlide: 1,
                        },
                        tablet: {
                          breakpoint: { max: 1024, min: 464 },
                          items: 2,
                          slidesToSlide: 1,
                        },
                        mobile: {
                          breakpoint: { max: 464, min: 0 },
                          items: 1,
                          slidesToSlide: 1,
                        },
                      }}
                      showDots={false}
                      keyBoardControl
                      customTransition="transform 500ms ease-in-out"
                      transitionDuration={500}
                      containerClass="digi_packs_content_list"
                      itemClass="col-lg-4 col-sm-6 digi_packs_carousel_item"
                    >
                      {products.map((product) => (
                        <div
                          key={product._id}
                          className="most_selling_art_box digi_packs_box d-flex flex-column"
                        >
                          <div className="most_selling_art_box_img position-relative">
                            {isLoggedIn && (
                              <button
                                type="button"
                                className="like_icon border-0 bg-transparent p-0"
                                onClick={(e) => {
                                  e.preventDefault(); // Prevent default anchor behavior
                                  e.stopPropagation(); // Prevent event bubbling
                                  handleWishlistClick(product?._id);
                                }}
                                aria-label="Add to wishlist"
                              >
                                <svg
                                  width="46"
                                  height="46"
                                  viewBox="0 0 46 46"
                                  fill="none"
                                  xmlns="http://www.w3.org/2000/svg"
                                >
                                  <rect
                                    width="46"
                                    height="46"
                                    rx="23"
                                    fill="#FAF6EE"
                                  />
                                  <path
                                    d="M29.8569 14.7447C25.8652 12.6038 22.9991 15.7597 22.9991 15.7597C22.9991 15.7597 20.1328 12.6038 16.1411 14.7447C11.3064 17.3377 10.9592 27.492 22.9991 32C35.0389 27.492 34.6916 17.3377 29.8569 14.7447Z"
                                    stroke="#112C3B"
                                    strokeWidth="1.5"
                                    strokeLinecap="round"
                                    fill={
                                      isProductInWishlist(product._id)
                                        ? "#112C3B"
                                        : "none"
                                    }
                                  />
                                </svg>
                              </button>
                            )}

                            <ImageWithLoader
                              src={
                                product.image || "/assets/images/nav_img.png"
                              }
                              alt={product.name}
                              className="w-100"
                              style={{
                                width: "460px",
                                height: "250px",
                                objectFit: "cover",
                                cursor: "pointer",
                              }}
                              onClick={() =>
                                navigate(`/product/${product._id}`)
                              }
                            />

                            <div className="common_multi_btn_area">
                              <button
                                type="button"
                                className="blue_btn light_btn"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  handleAddToCart(product._id);
                                }}
                                disabled={addingToCart}
                              >
                                {addingToCart ? "Adding..." : "Add to Cart"}
                              </button>
                              <button
                                type="button"
                                className="blue_btn"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  handleBuyNow(product._id);
                                }}
                                disabled={addingToCart}
                              >
                                {addingToCart ? "Processing..." : "Buy Now"}
                              </button>
                            </div>
                          </div>

                          <div className="nav_inner_details_box_info font_two color_darkblue">
                            <p className="fw_500">{product.name}</p>
                            <p className="nav_inner_details_info fw_500">
                              {product.artistId.name},{" "}
                              {product?.artistId?.country}
                            </p>
                            <div className="nav_inner_details_price_box">
                              <span className="nav_box_price fw_700">
                                ${product.price}
                              </span>
                              <span className="nav_box_cm fw_500">
                                {convertInchesToCm(
                                  product?.fileDetails.recommendedPrintSize,
                                )}
                              </span>
                              {/*{product.fileDetails?.recommendedPrintSize && (*/}
                              {/*  <span className="nav_box_cm fw_500">*/}
                              {/*    {product.fileDetails.recommendedPrintSize}*/}
                              {/*  </span>*/}
                              {/*)}*/}
                            </div>
                          </div>
                        </div>
                      ))}
                    </Carousel>
                  )}
                </div>
                <div className="col-12 d-lg-none d-flex justify-content-center">
                  <div className="title_arrow_box d-flex">
                    <button
                      onClick={goToPreviousExplore}
                      className="arrow-btn"
                      aria-label="Previous"
                    >
                      <img
                        src="/assets/images/nav_arrow_left.png"
                        alt="Previous"
                      />
                    </button>
                    <button
                      onClick={goToNextExplore}
                      className="arrow-btn"
                      aria-label="Next"
                    >
                      <img
                        src="/assets/images/nav_arrow_right.png"
                        alt="Next"
                      />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Full Image Modal */}
      {showFullImage && (
        <div
          className="full_image_modal"
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundColor: "rgba(0, 0, 0, 0.9)",
            zIndex: 9999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "20px",
          }}
          onClick={() => setShowFullImage(false)}
        >
          <button
            style={{
              position: "absolute",
              top: "20px",
              right: "20px",
              background: "white",
              border: "none",
              borderRadius: "50%",
              width: "40px",
              height: "40px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              zIndex: 10000,
            }}
            onClick={(e) => {
              e.stopPropagation();
              setShowFullImage(false);
            }}
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M18 6L6 18"
                stroke="#112C3B"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M6 6L18 18"
                stroke="#112C3B"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
          <img
            src={sliderImages[currentImageIndex]}
            alt="Full view"
            style={{
              maxWidth: "95%",
              maxHeight: "95%",
              objectFit: "contain",
              borderRadius: "8px",
            }}
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </>
  );
};

export default ProductDetails;
